import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, MapPin, Image as ImageIcon, Landmark, History, Plus, X, Send } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { compressImage } from "@/lib/compress-image";
import {
  getMyCities,
  getCityForEditor,
  submitCityChange,
  cancelCitySubmission,
} from "@/lib/city-teams.functions";

// Max raw upload we'll accept before compression (compressor targets ≤150 KB WebP/JPEG, ≤1920px).
const MAX_RAW_UPLOAD_BYTES = 20 * 1024 * 1024;


export const Route = createFileRoute("/_authenticated/city")({
  head: () => ({ meta: [
    { title: "City Team Dashboard — What Finland" },
    { name: "robots", content: "noindex,nofollow" },
  ] }),
  component: CityDashboard,
});

type Tab = "profile" | "gallery" | "poi" | "history";

function CityDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("profile");

  const myCitiesQ = useQuery({
    queryKey: ["city-teams", "my-cities"],
    queryFn: () => getMyCities(),
  });

  useEffect(() => {
    if (myCitiesQ.data && myCitiesQ.data.length && !selectedId) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setSelectedId((myCitiesQ.data[0] as any).id);
    }
  }, [myCitiesQ.data, selectedId]);

  const cityQ = useQuery({
    queryKey: ["city-teams", "city", selectedId],
    queryFn: () => getCityForEditor({ data: { cityId: selectedId! } }),
    enabled: !!selectedId,
  });

  async function signOut() {
    await supabase.auth.signOut();
    navigate({ to: "/login" });
  }

  if (myCitiesQ.isLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }
  if (myCitiesQ.error) {
    return <div className="p-8 text-sm text-rose-600">Failed to load your cities: {String(myCitiesQ.error)}</div>;
  }
  const cities = (myCitiesQ.data ?? []) as Array<{ id: string; name: string; slug: string }>;
  if (!cities.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <MapPin className="h-8 w-8 mx-auto text-slate-400" />
          <h1 className="mt-3 text-xl font-semibold text-slate-900">No City Assigned</h1>
          <p className="mt-2 text-sm text-slate-600">Your account isn't linked to any city yet. Ask a What Finland admin to invite you.</p>
          <button onClick={signOut} className="mt-6 text-sm text-slate-500 hover:text-slate-900">Sign Out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4 flex-wrap">
          <MapPin className="h-5 w-5 text-slate-900" />
          <h1 className="text-lg font-semibold text-slate-900">City Team Dashboard</h1>
          <select
            value={selectedId ?? ""}
            onChange={(e) => setSelectedId(e.target.value)}
            className="ml-auto rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
          >
            {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={signOut} className="text-sm text-slate-500 hover:text-slate-900">Sign Out</button>
        </div>
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 pb-2 flex gap-1 overflow-x-auto">
          {([
            ["profile", "Profile", MapPin],
            ["gallery", "Gallery", ImageIcon],
            ["poi", "Points Of Interest", Landmark],
            ["history", "Submissions", History],
          ] as const).map(([k, label, Icon]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}>
              <Icon className="h-4 w-4" /> {label}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {!selectedId || cityQ.isLoading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : cityQ.error ? (
          <div className="p-8 text-sm text-rose-600">Failed to load city: {String(cityQ.error)}</div>
        ) : cityQ.data ? (
          <>
            {tab === "profile" && <ProfileTab city={cityQ.data.city} onSubmit={() => qc.invalidateQueries({ queryKey: ["city-teams"] })} />}
            {tab === "gallery" && <GalleryTab cityId={selectedId} onSubmit={() => qc.invalidateQueries({ queryKey: ["city-teams"] })} />}
            {tab === "poi" && <PoiTab cityId={selectedId} places={cityQ.data.places} onSubmit={() => qc.invalidateQueries({ queryKey: ["city-teams"] })} />}
            {tab === "history" && <HistoryTab submissions={cityQ.data.submissions} onChanged={() => qc.invalidateQueries({ queryKey: ["city-teams"] })} />}
          </>
        ) : null}
      </main>
    </div>
  );
}

// ─── Profile Tab ────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ProfileTab({ city, onSubmit }: { city: any; onSubmit: () => void }) {
  const [form, setForm] = useState({
    name: city?.name ?? "",
    tagline: city?.tagline ?? "",
    region: city?.region ?? "",
    population: city?.population ?? "",
    founded: city?.founded ?? "",
    image_url: city?.image_url ?? "",
    accent: city?.accent ?? "",
  });
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      submitCityChange({ data: { cityId: city.id, kind: "profile", changes: payload, editorNote: note || undefined } }),
    onSuccess: () => { toast.success("Profile changes submitted for review."); setNote(""); onSubmit(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to submit"),
  });

  const changed = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(form)) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const orig = (city as any)?.[k] ?? "";
      if (String(orig ?? "") !== String(v ?? "")) {
        if (k === "founded") out[k] = v ? Number(v) : undefined;
        else out[k] = v || undefined;
      }
    }
    return out;
  }, [form, city]);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-900">City Profile</h2>
      <p className="text-sm text-slate-500 mt-1">Update these fields and submit for admin review. Nothing goes live until approved.</p>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          ["name","Name"],["tagline","Tagline"],["region","Region"],["population","Population"],
          ["founded","Founded (Year)"],["accent","Accent Colour"],["image_url","Hero Image URL"],
        ].map(([k, label]) => (
          <div key={k} className={k === "image_url" || k === "tagline" ? "md:col-span-2" : ""}>
            <label className="text-xs font-medium text-slate-600 block mb-1">{label}</label>
            <input
              value={String(form[k as keyof typeof form] ?? "")}
              onChange={(e) => setForm({ ...form, [k]: e.target.value })}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm"
            />
            {k === "image_url" && (
              <HeroUploader
                cityId={city?.id}
                currentUrl={String(form.image_url ?? "")}
                onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))}
              />
            )}
          </div>
        ))}
      </div>

      <div className="mt-4">
        <label className="text-xs font-medium text-slate-600 block mb-1">Note To Reviewer (Optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
      </div>
      <div className="mt-4 flex items-center justify-end gap-3">
        <div className="text-xs text-slate-500 mr-auto">{Object.keys(changed).length} field{Object.keys(changed).length === 1 ? "" : "s"} changed</div>
        <button
          disabled={!Object.keys(changed).length || mut.isPending}
          onClick={() => mut.mutate(changed)}
          className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-slate-800 disabled:opacity-40">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit For Review
        </button>
      </div>
    </div>
  );
}

// ─── Hero Uploader (with auto-optimization) ────────────────────────────────
function HeroUploader({ cityId, currentUrl, onUploaded }: { cityId?: string; currentUrl: string; onUploaded: (url: string) => void }) {
  const [busy, setBusy] = useState(false);
  async function handle(files: FileList | null) {
    const file = files?.[0];
    if (!file || !cityId) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > MAX_RAW_UPLOAD_BYTES) { toast.error("Image exceeds 20 MB."); return; }
    setBusy(true);
    try {
      const optimized = await compressImage(file);
      const ext = (optimized.name.split(".").pop() || "webp").toLowerCase();
      const path = `cities/${cityId}/hero/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage.from("content-images").upload(path, optimized, {
        contentType: optimized.type,
        cacheControl: "31536000",
        upsert: false,
      });
      if (upErr) throw upErr;
      const { data: pub } = supabase.storage.from("content-images").getPublicUrl(path);
      onUploaded(pub.publicUrl);
      toast.success("Hero image optimized and uploaded.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="mt-2 flex items-center gap-3">
      {currentUrl && <img src={currentUrl} alt="" className="h-14 w-20 object-cover rounded-lg border border-slate-200" />}
      <label className="inline-flex items-center gap-2 px-3 py-2 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 cursor-pointer">
        <input type="file" accept="image/*" className="hidden" onChange={(e) => handle(e.target.files)} />
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        {busy ? "Optimizing…" : currentUrl ? "Replace Hero Image" : "Upload Hero Image"}
      </label>
      <span className="text-[11px] text-slate-500">Auto-resized to 1920px · WebP · ≤150 KB</span>
    </div>
  );
}



// ─── Gallery Tab ────────────────────────────────────────────────────────────
function GalleryTab({ cityId, onSubmit }: { cityId: string; onSubmit: () => void }) {
  const [images, setImages] = useState<Array<{ url: string; caption?: string }>>([]);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const mut = useMutation({
    mutationFn: () => submitCityChange({ data: { cityId, kind: "gallery", changes: { images: images.map((im, i) => ({ ...im, sort_order: i })) }, editorNote: note || undefined } }),
    onSuccess: () => { toast.success("Gallery submitted for review."); setImages([]); setNote(""); onSubmit(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to submit"),
  });

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setUploading(true);
    try {
      const next = [...images];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) { toast.error(`${file.name} is not an image.`); continue; }
        if (file.size > MAX_RAW_UPLOAD_BYTES) { toast.error(`${file.name} exceeds 20 MB.`); continue; }
        let optimized: File;
        try {
          optimized = await compressImage(file);
        } catch (err) {
          toast.error(err instanceof Error ? err.message : `Could not process ${file.name}`);
          continue;
        }
        const ext = (optimized.name.split(".").pop() || "webp").toLowerCase();
        const path = `cities/${cityId}/gallery/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("content-images").upload(path, optimized, {
          contentType: optimized.type,
          cacheControl: "31536000",
          upsert: false,
        });
        if (upErr) { toast.error(upErr.message); continue; }
        const { data: pub } = supabase.storage.from("content-images").getPublicUrl(path);
        next.push({ url: pub.publicUrl });
      }
      setImages(next);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-900">Media Gallery</h2>
      <p className="text-sm text-slate-500 mt-1">Upload photos of your city. The first image becomes the hero once approved. Images are automatically resized and converted to WebP for fast loading — upload originals up to 20 MB and we'll optimize them.</p>


      <label className="mt-4 flex flex-col items-center justify-center h-32 border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50">
        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
        {uploading ? <Loader2 className="h-5 w-5 animate-spin text-slate-500" /> : <>
          <Plus className="h-5 w-5 text-slate-500" />
          <span className="mt-1 text-sm text-slate-600">Click to upload photos</span>
        </>}
      </label>

      {images.length > 0 && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {images.map((im, i) => (
            <div key={i} className="relative group aspect-square rounded-xl overflow-hidden bg-slate-100">
              <img src={im.url} alt="" className="w-full h-full object-cover" />
              <button onClick={() => setImages(images.filter((_, j) => j !== i))}
                className="absolute top-2 right-2 h-7 w-7 rounded-full bg-white/90 text-slate-900 flex items-center justify-center opacity-0 group-hover:opacity-100">
                <X className="h-4 w-4" />
              </button>
              {i === 0 && <span className="absolute bottom-2 left-2 text-[10px] font-bold uppercase tracking-wider bg-slate-900 text-white px-2 py-1 rounded">Hero</span>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-4">
        <label className="text-xs font-medium text-slate-600 block mb-1">Note To Reviewer (Optional)</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
          className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" />
      </div>
      <div className="mt-4 flex justify-end">
        <button
          disabled={!images.length || mut.isPending}
          onClick={() => mut.mutate()}
          className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-slate-800 disabled:opacity-40">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit For Review
        </button>
      </div>
    </div>
  );
}

// ─── POI Tab ────────────────────────────────────────────────────────────────
function PoiTab({ cityId, places, onSubmit }: { cityId: string; places: Array<{ id: string; name: string; category: string | null; published: boolean }>; onSubmit: () => void }) {
  const [form, setForm] = useState({ name: "", category: "", address: "", description: "", image_url: "" });
  const [note, setNote] = useState("");
  const mut = useMutation({
    mutationFn: () => submitCityChange({ data: { cityId, kind: "poi", changes: { ...form }, editorNote: note || undefined } }),
    onSuccess: () => { toast.success("Point of interest submitted for review."); setForm({ name: "", category: "", address: "", description: "", image_url: "" }); setNote(""); onSubmit(); },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to submit"),
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-lg font-semibold text-slate-900">Add A Point Of Interest</h2>
        <p className="text-sm text-slate-500 mt-1">Attractions, landmarks, activities. New POIs go to admin review before appearing on the site.</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="text-xs font-medium text-slate-600 block mb-1">Name</label>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" /></div>
          <div><label className="text-xs font-medium text-slate-600 block mb-1">Category</label>
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Sauna, Attraction, Nature" className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="text-xs font-medium text-slate-600 block mb-1">Address</label>
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="text-xs font-medium text-slate-600 block mb-1">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="text-xs font-medium text-slate-600 block mb-1">Image URL</label>
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" /></div>
          <div className="md:col-span-2"><label className="text-xs font-medium text-slate-600 block mb-1">Note To Reviewer (Optional)</label>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm" /></div>
        </div>
        <div className="mt-4 flex justify-end">
          <button disabled={!form.name || mut.isPending} onClick={() => mut.mutate()}
            className="inline-flex items-center gap-2 bg-slate-900 text-white text-sm font-medium px-4 py-2 rounded-xl hover:bg-slate-800 disabled:opacity-40">
            {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit For Review
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h3 className="text-sm font-semibold text-slate-900">Existing Points Of Interest ({places.length})</h3>
        {places.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing here yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {places.map((p) => (
              <li key={p.id} className="py-2 flex items-center gap-3">
                <span className="text-sm text-slate-900 flex-1">{p.name}</span>
                <span className="text-xs text-slate-500">{p.category || "—"}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                  {p.published ? "Live" : "Draft"}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─── History Tab ────────────────────────────────────────────────────────────
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function HistoryTab({ submissions, onChanged }: { submissions: any[]; onChanged: () => void }) {
  async function cancel(id: string) {
    try {
      await cancelCitySubmission({ data: { id } });
      toast.success("Cancelled.");
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed");
    }
  }
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
      <h2 className="text-lg font-semibold text-slate-900">Your Submissions</h2>
      {submissions.length === 0 ? (
        <p className="mt-2 text-sm text-slate-500">Nothing submitted yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {submissions.map((s) => (
            <li key={s.id} className="py-3 flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-900 capitalize">{s.kind}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${
                    s.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                    s.status === "rejected" ? "bg-rose-100 text-rose-700" :
                    s.status === "submitted" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>{s.status}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{new Date(s.created_at).toLocaleString()}</p>
                {s.admin_note && <p className="text-xs text-slate-700 mt-1"><strong>Note:</strong> {s.admin_note}</p>}
              </div>
              {s.status === "submitted" && (
                <button onClick={() => cancel(s.id)} className="text-xs text-slate-500 hover:text-slate-900">Cancel</button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
